import { Location } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AccountActions, EnvironmentActions } from '@app/core/actions';
import { GoogleTagManagerService } from '@app/core/lib/google-tag-manager/angular-google-tag-manager.service';
import * as fromRoot from '@app/reducers';
import { getCurrentPath } from '@app/shared/helpers/location';
import { Plan } from '@app/shared/models/plan';
import { DialogService } from '@ngneat/dialog';
import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Observable, ReplaySubject, Subject } from 'rxjs';
import { distinctUntilChanged, first, map, mergeMap, withLatestFrom } from 'rxjs/operators';
import { Constant } from 'src/constant';
import * as fromSignup from '../reducers';
import { SubscriptionActions } from './actions';
import { ModalSignupProcessComponent } from './modals/modal-signup-process/modal-signup-process.component';

@Component({
  selector: 'app-subscription',
  templateUrl: './subscription.component.html',
  styleUrls: ['./subscription.component.scss']
})
export class SubscriptionComponent implements OnInit {

  canDisplaySignupForm$: Observable<boolean>;
  language$: Observable<string>;
  planGroup$: Observable<any[]>;
  computedPlan$: Observable<Plan>;
  planName: string;
  planMaxUsers: number;
  countries$: Observable<any[]>;
  countryCode$: Observable<any>;
  defaultCountry$: Observable<any[]>;
  account$: Observable<any>;

  signupProcessing$: Observable<boolean>;
  signupSucceed$: Observable<boolean>;
  signupFailed$: Observable<boolean>;
  signupError$: Observable<any>;

  updateAccountProcessing$: Observable<boolean>;
  updateAccountSucceed$: Observable<boolean>;
  updateAccountFailed$: Observable<boolean>;
  updateAccountError$: Observable<boolean>;

  updateBillingInformationsProcessing$: Observable<boolean>;
  updateBillingInformationsFailed$: Observable<boolean>;
  updateBillingInformationsError$: Observable<boolean>;

  updateSubscriptionProcessing$: Observable<boolean>;

  paymentState$: Observable<any>;
  paymentProcessing$: Observable<boolean>;
  paymentFailed$: Observable<boolean>;

  subscription$: Observable<any>;
  errorSubscription$: Observable<string>;

  billingsInformations$: Observable<any>;

  stripeLoaded$: Observable<boolean>;

  hasPaypalAgreement$ = new Subject<boolean>();
  environment$: Observable<any>;

  window;
  formData: any;

  constructor(
    private store$: Store<fromRoot.State & fromSignup.State>,
    private route: ActivatedRoute,
    private dialog: DialogService,
    private location: Location,
    private translateService: TranslateService,
    private gtmService: GoogleTagManagerService,
  ) {

    this.stripeLoaded$ = this.store$.select(fromRoot.getStripeLoaded);

    this.route.params.pipe(mergeMap(params => {
      return [
        SubscriptionActions.loadPlans({ id: params.id }),
        AccountActions.loadBillingInformations(),
        EnvironmentActions.loadCountries(),
        EnvironmentActions.LoadStripe(),
      ];
    })).subscribe(action => {
      this.store$.dispatch(action);
    });

    this.canDisplaySignupForm$ = this.store$.pipe(withLatestFrom(
      this.store$.pipe(select(fromRoot.getCountriesLoaded)),
      this.store$.pipe(select(fromRoot.getBillingInformationsLoaded)),
      this.store$.pipe(select(fromSignup.getPlanGroupLoaded))
    ), map(([store, countriesLoaded, billingsInformationsLoaded, planGroupLoaded]) => countriesLoaded && billingsInformationsLoaded && planGroupLoaded));

    this.signupProcessing$ = this.store$.pipe(select(fromSignup.getSignupProcessing));
    this.signupSucceed$ = this.store$.pipe(select(fromSignup.getSignupSucceed));
    this.signupFailed$ = this.store$.pipe(select(fromSignup.getSignupFailed));
    this.signupError$ = this.store$.pipe(select(fromSignup.getSignupError));

    this.updateAccountProcessing$ = this.store$.pipe(select(fromSignup.getUpdateAccountProcessing));
    this.updateAccountSucceed$ = this.store$.pipe(select(fromSignup.getUpdateAccountSucceed));
    this.updateAccountFailed$ = this.store$.pipe(select(fromSignup.getUpdateAccountFailed));

    this.updateBillingInformationsProcessing$ = this.store$.pipe(select(fromSignup.getUpdateBillingInformationsProcessing));
    this.updateBillingInformationsFailed$ = this.store$.pipe(select(fromSignup.getUpdateBillingInformationsFailed));
    this.updateBillingInformationsError$ = this.store$.pipe(select(fromSignup.getUpdateBillingInformationsError));

    this.updateSubscriptionProcessing$ = this.store$.pipe(select(fromSignup.getUpdateSubscriptionProcessing));

    this.paymentProcessing$ = this.store$.pipe(select(fromRoot.getPaymentProcessing));

    this.paymentFailed$ = this.store$.pipe(select(fromRoot.getPaymentFailed));
    this.paymentState$ = this.store$.pipe(select(fromRoot.getPaymentState));
    this.paymentState$.pipe(
      first(state => state.paymentSucceed),
      withLatestFrom(
        this.store$.select(fromRoot.getBillingInformations),
        this.store$.select(fromRoot.getPlan),
        this.store$.select(fromRoot.getActiveIdentity),
      )
    ).subscribe(([state, informations, plan, identity]) => {
      this.gtmService.pushTag({
        event: Constant.eventGTM.SUBSCRIPTION,
        payment_provider: 'Stripe',
        billing_informations_city: informations.city,
        billing_informations_zipCode: informations.zipCode,
        billing_informations_company: informations.company,
        billing_informations_address: informations.address,
        billing_informations_lastName: informations.lastName,
        billing_informations_firstName: informations.firstName,
        billing_informations_countryCode: informations.countryCode,
        plan_id: plan.id,
        plan_name: plan.name,
        plan_description: plan.description,
        plan_currency: plan.currency,
        plan_duration: plan.duration,
        plan_price_excl_tax: plan.priceExclTax,
        plan_max_active_user: plan.maxActiveUser,
        username: identity.username,
      });
    });

    this.account$ = this.store$.select(fromRoot.getActiveIdentity);
    this.planGroup$ = this.store$.select(fromSignup.getPlans);
    this.billingsInformations$ = this.store$.select(fromRoot.getBillingInformations);
    this.countries$ = this.store$.select(fromRoot.getCountries);
    this.countryCode$ = this.store$.select(fromRoot.getCountryCode);
    this.subscription$ = this.store$.select(fromRoot.getSubscription);
    this.computedPlan$ = this.store$.select(fromSignup.getComputedPlan);
    this.language$ = this.store$.select(fromRoot.getSelectedLanguage);

    this.planGroup$.subscribe(planGroup => {
      if (planGroup && planGroup.length) {
        this.planName = planGroup[0].name;
        this.planMaxUsers = planGroup[0].maxActiveUser;
      }
    });

    this.environment$ = this.store$.select(fromRoot.getEnvironmentState);

    this.hasPaypalAgreement().subscribe((paypal: any) => {
      if (paypal && paypal.approvalUrl) {
        window.location.href = paypal.approvalUrl;
      }
    });

  }

  ngOnInit(): void {
    this.redirectLangUrl();
  }

  redirectLangUrl() {
    const userLang = this.translateService.currentLang;
    if (userLang === Constant.languageFallback) {
      return;
    }
    this.location.go(userLang + '/' + getCurrentPath(window.location.pathname));
  }

  hasPaypalAgreement(): Observable<boolean> {
    const hasPaypalAgreement$ = new ReplaySubject<boolean>();
    this.store$.select(fromRoot.getPaypalAgreement)
      .pipe(distinctUntilChanged())
      .subscribe((paypal) => {
        if (paypal && paypal.approvalUrl) {
          hasPaypalAgreement$.next(paypal);
          hasPaypalAgreement$.complete();
        } else {
          hasPaypalAgreement$.next(false);
        }
      });
    return hasPaypalAgreement$.asObservable();
  }

  updateFormData(data: any) {
    this.store$.dispatch(SubscriptionActions.updateFormData({ data }));
    this.formData = data; // ?
    this.computePlan();
  }

  private computePlan() {
    this.store$.dispatch(SubscriptionActions.computePlan());
  }

  signUp() {
    this.store$.dispatch(SubscriptionActions.signup());
    this.openModalProcess();
  }

  openModalProcess() {
    this.dialog.open(ModalSignupProcessComponent, {
      closeButton: false,
      enableClose: false,
      size: 'sm'
    });
  }

}


